import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { School } from '../../../models/school/school';
import {ROUTES} from "../../../constants/routes";

interface SchoolCardProps {
  school: School;
  className?: string;
  showEnrollmentProgress?: boolean;
}

const SchoolCard: React.FC<SchoolCardProps> = ({
  school,
  className = '',
  showEnrollmentProgress = true
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div 
      className={`relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 md:p-12 border border-gray-200 shadow-2xl group overflow-hidden ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-300 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-radial from-gray-300 to-transparent rounded-full transform translate-x-32 translate-y-32"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-4 md:mb-0">
            {/* Professional Logo Display */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg border border-gray-200">
                <img
                  src={school.media.logo?.url || ''}
                  alt={school.title}
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<div class="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center text-gray-700 text-lg font-bold">${school.title.charAt(0)}</div>`;
                    }
                  }}
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black">{school.title}</h3>
                <p className="text-sm text-gray-500">{t('pages.schools.transformativeEducation', 'Transformative Education')}</p>
              </div>
            </div>
          </div>

          {/* Featured Badge */}
          <div className="hidden md:block">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700 border border-gray-300">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.070 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span>{t('pages.schools.featuredProgram')}</span>
            </span>
          </div>
        </div>

        {/* School Info Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Left Column - Main Info */}
          <div>
            <p className="text-gray-600 text-base leading-relaxed mb-6">
              {school.description}
            </p>

            {/* Key Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                </div>
                <span className="font-medium text-black">{t('pages.schools.location')}:</span>
                <span>{school.address.name}</span>
              </div>

              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium text-black">{t('pages.schools.duration')}:</span>
                <span>{school.duration}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Features & CTA */}
          <div>
            {/* Key Features */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-black mb-4">
                {t('pages.schools.keyFeatures', 'What You\'ll Experience')}
              </h4>
              <div className="space-y-3">
                {school.benefits?.slice(0, 3).map((benefit: string, index: number) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600 text-sm leading-relaxed">
                      {benefit}
                    </span>
                  </div>
                ))}
                {school.benefits && school.benefits.length > 3 && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                    <span>+{school.benefits.length - 3}</span>
                    <span>{t('general.actions.readMore', 'more activities')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Button */}
            <Link
              to={`${ROUTES.SCHOOLS}/${school.key}/${school.id}`}
              className="inline-flex items-center justify-center gap-2 w-full bg-black text-white hover:bg-gray-800 py-4 px-6 rounded-lg transition-all duration-300 font-semibold hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>{t('pages.schools.learnMore', 'Learn More')}</span>
              <svg className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolCard;
